import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFeatured, getCategories, getActiveBanners } from '../utils/api';
import { useSEO } from '../utils/useSEO';
import ProductCard from '../components/common/ProductCard';
import { FiArrowRight, FiTruck, FiShield, FiRefreshCw, FiHeadphones, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import styles from './HomePage.module.css';

const DEFAULT_BANNERS = [
  { _id:'1', title:'ঈদ স্পেশাল সেল 🎉', subtitle:'Up to 50% off on electronics, fashion & more!', buttonText:'Shop the Sale', buttonLink:'/products?featured=true', bgColor:'linear-gradient(135deg,#006A4E 0%,#00875f 100%)', image:'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=500' },
  { _id:'2', title:'Latest Electronics 📱', subtitle:'New arrivals: Samsung, Walton, Xiaomi. Best prices in BD.', buttonText:'Explore Now', buttonLink:'/products?category=electronics', bgColor:'linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)', image:'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=500' },
  { _id:'3', title:'Authentic Jamdani 🧵', subtitle:'Handwoven sarees from Narayanganj. GI-tagged genuine craft.', buttonText:'Shop Fashion', buttonLink:'/products?category=fashion', bgColor:'linear-gradient(135deg,#7c3aed 0%,#a855f7 100%)', image:'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500' },
];

const FEATURES = [
  { icon:FiTruck,      title:'Fast Delivery',  desc:'Same day in Dhaka',   color:'#006A4E' },
  { icon:FiShield,     title:'100% Genuine',   desc:'Authentic products',  color:'#2563eb' },
  { icon:FiRefreshCw,  title:'Easy Returns',   desc:'7-day return policy', color:'#d97706' },
  { icon:FiHeadphones, title:'24/7 Support',   desc:'Call us: 16xxx',      color:'#7c3aed' },
];

function SkeletonCard() {
  return (
    <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', border:'1px solid var(--border)' }}>
      <div className="skeleton" style={{ aspectRatio:1 }} />
      <div style={{ padding:12, display:'flex', flexDirection:'column', gap:8 }}>
        <div className="skeleton" style={{ height:12, width:'60%' }} />
        <div className="skeleton" style={{ height:14, width:'90%' }} />
        <div className="skeleton" style={{ height:32, marginTop:4 }} />
      </div>
    </div>
  );
}

export default function HomePage() {
  useSEO({
    title: 'BDShop — বাংলাদেশের সেরা অনলাইন শপ',
    description: 'Shop electronics, fashion, food & more. bKash, Nagad, Cash on Delivery. বাংলাদেশের সেরা অনলাইন শপিং।',
    keywords: 'online shopping bangladesh, bdshop, bkash, electronics, fashion, food',
  });

  const [banners, setBanners]       = useState(DEFAULT_BANNERS);
  const [featured, setFeatured]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [idx, setIdx]               = useState(0);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    Promise.all([getFeatured(), getCategories(), getActiveBanners()])
      .then(([fRes, cRes, bRes]) => {
        setFeatured(fRes.data);
        setCategories(cRes.data.slice(0, 8));
        if (bRes.data?.length > 0) setBanners(bRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % banners.length), 5500);
    return () => clearInterval(t);
  }, [banners.length]);

  const b = banners[idx] || banners[0];

  return (
    <main>
      {/* ── HERO ── */}
      <section className={styles.hero} style={{ background: b.bgColor || 'var(--primary)' }} aria-label="Hero banner">
        <div className={`container ${styles.heroInner}`}>
          <div className={styles.heroText}>
            <h1 className={styles.heroTitle}>{b.title}</h1>
            <p className={styles.heroSub}>{b.subtitle}</p>
            <div className={styles.heroBtns}>
              <Link to={b.buttonLink || '/products'} className="btn btn-white btn-xl">
                {b.buttonText || 'Shop Now'} <FiArrowRight/>
              </Link>
              <Link to="/products" className="btn btn-xl" style={{ background:'rgba(255,255,255,.15)', color:'#fff', borderColor:'rgba(255,255,255,.4)' }}>
                Browse All
              </Link>
            </div>
            <div className={styles.heroBadges}>
              <span>✅ Cash on Delivery</span>
              <span>✅ bKash · Nagad</span>
              <span>✅ 7-day Returns</span>
            </div>
          </div>
          {b.image && (
            <div className={styles.heroImgWrap} aria-hidden="true">
              <img src={b.image} alt="" className={styles.heroImg}/>
            </div>
          )}
        </div>

        {banners.length > 1 && (
          <div className={styles.sliderCtrl}>
            <button onClick={() => setIdx(i => (i-1+banners.length)%banners.length)} className={styles.sliderBtn}><FiChevronLeft size={18}/></button>
            <div className={styles.sliderDots}>
              {banners.map((_,i) => <button key={i} className={`${styles.sDot} ${i===idx?styles.sDotActive:''}`} onClick={() => setIdx(i)}/>)}
            </div>
            <button onClick={() => setIdx(i => (i+1)%banners.length)} className={styles.sliderBtn}><FiChevronRight size={18}/></button>
          </div>
        )}
      </section>

      {/* ── FEATURES ── */}
      <section className={styles.featuresBar} aria-label="Key features">
        <div className="container">
          <div className={styles.featuresGrid}>
            {FEATURES.map(f => (
              <div key={f.title} className={styles.featItem}>
                <div className={styles.featIcon} style={{ background:`${f.color}18`, color:f.color }}><f.icon size={20}/></div>
                <div><p className={styles.featTitle}>{f.title}</p><p className={styles.featDesc}>{f.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CATEGORIES ── */}
      {categories.length > 0 && (
        <section className={styles.section} aria-label="Categories">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Shop by <span>Category</span></h2>
              <Link to="/products" className="see-all-btn">All <FiArrowRight size={13}/></Link>
            </div>
            <div className={styles.catGrid} role="list">
              {categories.map(cat => (
                <Link key={cat._id} to={`/products?category=${cat._id}`} className={styles.catCard} role="listitem">
                  <span className={styles.catEmoji}>{cat.icon || '🛍️'}</span>
                  <span className={styles.catName}>{cat.name}</span>
                  {cat.nameBn && <span className={styles.catBn}>{cat.nameBn}</span>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FEATURED ── */}
      <section className={styles.section} aria-labelledby="featured-h">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" id="featured-h">🔥 Featured <span>Products</span></h2>
            <Link to="/products?featured=true" className="see-all-btn">See All <FiArrowRight size={13}/></Link>
          </div>
          {loading ? (
            <div className="products-grid">{[...Array(8)].map((_,i) => <SkeletonCard key={i}/>)}</div>
          ) : featured.length > 0 ? (
            <div className="products-grid">{featured.map(p => <ProductCard key={p._id} product={p}/>)}</div>
          ) : (
            <p style={{ textAlign:'center', padding:'3rem', color:'var(--text-muted)' }}>
              No featured products yet. <Link to="/products" style={{ color:'var(--primary)' }}>Browse all →</Link>
            </p>
          )}
        </div>
      </section>

      {/* ── PROMO BANNERS ── */}
      <section className={styles.section} aria-label="Promotions">
        <div className="container">
          <div className={styles.promoGrid}>
            <div className={styles.promoCard} style={{ background:'linear-gradient(135deg,#E8192C,#c01424)' }}>
              <div className={styles.promoContent}><span>📦</span><div><h3>Cash on Delivery</h3><p>Pay when you receive. No advance!</p></div></div>
              <Link to="/products" className="btn btn-white btn-sm">Order Now</Link>
            </div>
            <div className={styles.promoCard} style={{ background:'linear-gradient(135deg,#006A4E,#00503b)' }}>
              <div className={styles.promoContent}><span>🇧🇩</span><div><h3>Made in Bangladesh</h3><p>Support local artisans</p></div></div>
              <Link to="/products" className="btn btn-white btn-sm">Explore</Link>
            </div>
            <div className={styles.promoCard} style={{ background:'linear-gradient(135deg,#7c3aed,#a855f7)' }}>
              <div className={styles.promoContent}><span>💳</span><div><h3>bKash & Nagad</h3><p>Instant mobile payment</p></div></div>
              <Link to="/products" className="btn btn-white btn-sm">Pay Easy</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── NEW ARRIVALS ── */}
      <section style={{ background:'#fff', padding:'2.5rem 0' }} aria-labelledby="new-h">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title" id="new-h">🆕 New <span>Arrivals</span></h2>
            <Link to="/products?sort=newest" className="see-all-btn">See All <FiArrowRight size={13}/></Link>
          </div>
          {loading ? (
            <div className="products-grid">{[...Array(4)].map((_,i) => <SkeletonCard key={i}/>)}</div>
          ) : (
            <div className="products-grid">{featured.slice(0,4).map(p => <ProductCard key={p._id} product={p}/>)}</div>
          )}
        </div>
      </section>
    </main>
  );
}
